import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Wand2, Image, Video, Mic, FileText, Download, Link2, Trash2, Dices,
  Pencil, Maximize, Cpu, Loader2, CheckCircle, AlertCircle, Clock,
  Play, Pause, Square, Volume2, Copy, Sparkles,
  ChevronDown, BookOpen, Type, Palette, Zap, Globe, Languages
} from 'lucide-react'
import { cn, ASPECT_RATIOS, IMAGE_STYLES, QUALITY_LEVELS, AI_MODELS, VIDEO_MODELS, VIDEO_STYLES, RANDOM_PROMPTS, TTS_LANGUAGES, SCRIPT_TEMPLATES } from '@/lib/utils'
import type { ScriptTemplateDef } from '@/lib/utils'
import { useImageGenerator } from '@/hooks/useImageGenerator'
import { useVideoGenerator } from '@/hooks/useVideoGenerator'
import { useTTS } from '@/hooks/useTTS'
import { useScriptGenerator } from '@/hooks/useScriptGenerator'
import { useToast } from '@/hooks/useToast'

export function Studio() {
  const [activeTool, setActiveTool] = useState<'image' | 'video' | 'tts' | 'script'>('image')

  return (
    <section id="studio" className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            AI Studio
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
            Creative Studio
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Choose your creative tool and start generating
          </p>
        </div>

        {/* Tool Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {[
            { id: 'image' as const, label: 'Image', icon: Image, desc: 'AI Image Generation' },
            { id: 'video' as const, label: 'Video', icon: Video, desc: 'AI Video Generation' },
            { id: 'tts' as const, label: 'Voice', icon: Mic, desc: 'Text to Speech' },
            { id: 'script' as const, label: 'Script', icon: FileText, desc: 'Script Writer' },
          ].map((tool) => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={cn(
                'flex items-center gap-3 px-6 py-4 rounded-xl border transition-all duration-300 min-w-[160px]',
                activeTool === tool.id
                  ? 'bg-primary/10 border-primary/30 text-primary shadow-lg shadow-primary/10'
                  : 'bg-card border-border text-muted-foreground hover:border-primary/20 hover:text-foreground'
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                activeTool === tool.id ? 'bg-primary/20' : 'bg-muted'
              )}>
                <tool.icon className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-sm">{tool.label}</div>
                <div className="text-xs opacity-60">{tool.desc}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Tool Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTool}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTool === 'image' && <ImageGenerator />}
            {activeTool === 'video' && <VideoGenerator />}
            {activeTool === 'tts' && <TTSGenerator />}
            {activeTool === 'script' && <ScriptGenerator />}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}

// ===== IMAGE GENERATOR =====
function ImageGenerator() {
  const [prompt, setPrompt] = useState('')
  const [ratio, setRatio] = useState('16:9')
  const [style, setStyle] = useState('')
  const [quality, setQuality] = useState('standard')
  const [model, setModel] = useState('flux')

  const { show } = useToast()
  const { generate, clear, isGenerating, result, error, progress } = useImageGenerator()

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      show('Please enter a description', 'error')
      return
    }
    const res = await generate({ prompt, ratio, style, quality, model })
    if (res) show('Image generated!', 'success')
  }

  const handleDownload = async () => {
    if (!result?.url) return
    try {
      const response = await fetch(result.url)
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `lumina-image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      show('Downloaded!', 'success')
    } catch {
      window.open(result.url, '_blank')
    }
  }

  const handleCopy = async () => {
    if (!result?.url) return
    try {
      await navigator.clipboard.writeText(result.url)
      show('URL copied!', 'success')
    } catch {
      show('Failed to copy', 'error')
    }
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-2xl shadow-primary/5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      {/* Prompt */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold mb-2">
          <Pencil className="w-4 h-4 text-primary" />
          Describe your image
        </label>
        <div className="relative">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A futuristic cyberpunk city at night with neon lights reflecting on wet streets..."
            maxLength={1000}
            rows={4}
            className="w-full px-5 py-4 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 resize-y min-h-[120px] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
          />
          <div className="absolute bottom-3 right-3 flex gap-1.5">
            <button onClick={() => { setPrompt(''); clear() }} className="w-8 h-8 rounded-lg bg-background/80 border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-all" title="Clear">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { setPrompt(RANDOM_PROMPTS[Math.floor(Math.random() * RANDOM_PROMPTS.length)]); show('Random prompt loaded!', 'info') }} className="w-8 h-8 rounded-lg bg-background/80 border border-border flex items-center justify-center text-muted-foreground hover:text-primary transition-all" title="Random">
              <Dices className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground text-right">{prompt.length}/1000</div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SelectField label="Aspect Ratio" icon={<Maximize className="w-4 h-4" />} value={ratio} onChange={setRatio} options={ASPECT_RATIOS.map(r => ({ label: `${r.label} (${r.value})`, value: r.value }))} />
        <SelectField label="Style" icon={<Palette className="w-4 h-4" />} value={style} onChange={setStyle} options={IMAGE_STYLES.map(s => ({ label: s.label, value: s.value }))} />
        <SelectField label="Quality" icon={<Zap className="w-4 h-4" />} value={quality} onChange={setQuality} options={QUALITY_LEVELS.map(q => ({ label: q.label, value: q.value }))} />
        <SelectField label="AI Model" icon={<Cpu className="w-4 h-4" />} value={model} onChange={setModel} options={AI_MODELS.map(m => ({ label: m.label, value: m.value }))} />
      </div>

      {/* Generate Button */}
      <button onClick={handleGenerate} disabled={isGenerating} className={cn('w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all duration-300', isGenerating ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5')}>
        {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating... {progress.toFixed(0)}%</> : <><Wand2 className="w-5 h-5" /> Generate Image</>}
      </button>

      {/* Loading */}
      {isGenerating && (
        <div className="mt-6 text-center py-8">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-3 border-primary/20" />
            <div className="absolute inset-0 rounded-full border-3 border-t-primary animate-spin" />
          </div>
          <p className="text-lg font-medium">Creating your masterpiece...</p>
          <div className="mt-4 max-w-xs mx-auto h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-6 p-5 rounded-xl bg-destructive/10 border border-destructive/20 text-center">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-2" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Result */}
      {result && !isGenerating && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <CheckCircle className="w-5 h-5 text-green-500" /> Your Image is Ready
            </div>
            <div className="flex gap-2">
              <button onClick={handleDownload} className="px-4 py-2 rounded-lg bg-muted border border-border text-sm font-medium hover:bg-muted/80 transition-colors flex items-center gap-2">
                <Download className="w-4 h-4" /> Download
              </button>
              <button onClick={handleCopy} className="px-4 py-2 rounded-lg bg-muted border border-border text-sm font-medium hover:bg-muted/80 transition-colors flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Copy Link
              </button>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-border bg-muted group">
            <img src={result.url} alt="Generated" className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex gap-4 text-xs text-white/70">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {result.generationTime.toFixed(1)}s</span>
                <span className="flex items-center gap-1"><Maximize className="w-3 h-3" /> {result.width}x{result.height}</span>
                <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> {result.model}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

// ===== VIDEO GENERATOR =====
function VideoGenerator() {
  const [prompt, setPrompt] = useState('')
  const [ratio, setRatio] = useState('16:9')
  const [style, setStyle] = useState('cinematic')
  const [model, setModel] = useState('seedance')
  const [duration, setDuration] = useState(5)
  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  const { show } = useToast()
  const { generate, isGenerating, result, error, progress } = useVideoGenerator()

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      show('Please enter a description', 'error')
      return
    }
    if (model !== 'seedance' && !apiKey) {
      setShowApiKey(true)
      show('API key required for this model', 'error')
      return
    }
    const res = await generate({ prompt, ratio, style, model, duration })
    if (res) show('Video generation started!', 'info')
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-2xl shadow-primary/5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-secondary/40 to-transparent" />

      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold mb-2">
          <Pencil className="w-4 h-4 text-secondary" />
          Describe your video
        </label>
        <div className="relative">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A cinematic shot of a cyberpunk city at night, neon lights reflecting on wet streets, slow camera pan..."
            maxLength={1000}
            rows={4}
            className="w-full px-5 py-4 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 resize-y min-h-[120px] focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary/40 transition-all"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SelectField label="Aspect Ratio" icon={<Maximize className="w-4 h-4" />} value={ratio} onChange={setRatio} options={ASPECT_RATIOS.map(r => ({ label: `${r.label} (${r.value})`, value: r.value }))} />
        <SelectField label="Video Style" icon={<Video className="w-4 h-4" />} value={style} onChange={setStyle} options={VIDEO_STYLES.map(s => ({ label: s.label, value: s.value }))} />
        <SelectField label="AI Model" icon={<Cpu className="w-4 h-4" />} value={model} onChange={setModel} options={VIDEO_MODELS.map(m => ({ label: m.label, value: m.value }))} />
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Clock className="w-4 h-4 text-secondary" />
            Duration: {duration}s
          </label>
          <input type="range" min="3" max="10" value={duration} onChange={(e) => setDuration(Number(e.target.value))} className="w-full h-2 rounded-lg bg-muted appearance-none cursor-pointer accent-secondary" />
        </div>
      </div>

      {showApiKey && (
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-sm font-medium text-amber-200 mb-2">API Key Required</p>
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="Enter your API key..." className="w-full px-4 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-secondary/30" />
        </div>
      )}

      <button onClick={handleGenerate} disabled={isGenerating} className={cn('w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all duration-300', isGenerating ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-gradient-to-r from-secondary to-secondary/80 text-white shadow-lg shadow-secondary/25 hover:shadow-secondary/40 hover:-translate-y-0.5')}>
        {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating Video... {progress.toFixed(0)}%</> : <><Video className="w-5 h-5" /> Generate Video</>}
      </button>

      {isGenerating && (
        <div className="mt-6 text-center py-8">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-3 border-secondary/20" />
            <div className="absolute inset-0 rounded-full border-3 border-t-secondary animate-spin" />
          </div>
          <p className="text-lg font-medium">Creating your video...</p>
          <p className="text-sm text-muted-foreground mt-1">This may take 1-3 minutes</p>
          <div className="mt-4 max-w-xs mx-auto h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-secondary to-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 p-5 rounded-xl bg-destructive/10 border border-destructive/20 text-center">
          <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-2" />
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}

      {result && !isGenerating && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <CheckCircle className="w-5 h-5 text-green-500" /> Video Ready
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden border border-border bg-muted">
            <div className="aspect-video flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Video generation template ready</p>
                <p className="text-sm mt-2">Add your API key to generate actual videos</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  )
}

// ===== TTS GENERATOR =====
function TTSGenerator() {
  const [text, setText] = useState('')
  const [lang, setLang] = useState('en-US')
  const [rate, setRate] = useState(1)
  const [pitch, setPitch] = useState(1)
  const [volume, setVolume] = useState(1)

  const { show } = useToast()
  const { voices, isSpeaking, isPaused, selectedVoice, setSelectedVoice, speak, pause, resume, stop, getVoicesByLang } = useTTS()

  const filteredVoices = getVoicesByLang(lang.split('-')[0])

  const handleSpeak = () => {
    if (!text.trim()) {
      show('Please enter text to speak', 'error')
      return
    }
    try {
      speak({ text, voice: selectedVoice, rate, pitch, volume, lang })
      show('Speaking...', 'info')
    } catch (err) {
      show('Speech synthesis not supported', 'error')
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    show('Text copied!', 'success')
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-2xl shadow-primary/5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

      {/* Text Input */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold mb-2">
          <Type className="w-4 h-4 text-accent" />
          Text to speak
        </label>
        <div className="relative">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter text to convert to speech..."
            maxLength={5000}
            rows={6}
            className="w-full px-5 py-4 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 resize-y min-h-[150px] focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 transition-all"
          />
          <div className="absolute bottom-3 right-3 flex gap-1.5">
            <button onClick={() => setText('')} className="w-8 h-8 rounded-lg bg-background/80 border border-border flex items-center justify-center text-muted-foreground hover:text-accent transition-all" title="Clear">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={handleCopy} className="w-8 h-8 rounded-lg bg-background/80 border border-border flex items-center justify-center text-muted-foreground hover:text-accent transition-all" title="Copy">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="mt-1.5 text-xs text-muted-foreground text-right">{text.length}/5000</div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Globe className="w-4 h-4 text-accent" />
            Language
          </label>
          <div className="relative">
            <select value={lang} onChange={(e) => { setLang(e.target.value); setSelectedVoice('') }} className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-accent/30 cursor-pointer">
              {TTS_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Mic className="w-4 h-4 text-accent" />
            Voice ({filteredVoices.length} available)
          </label>
          <div className="relative">
            <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-accent/30 cursor-pointer">
              <option value="">Default Voice</option>
              {filteredVoices.map((v, i) => (
                <option key={i} value={v.name}>{v.name} {v.gender ? `(${v.gender})` : ''}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
          </div>
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Zap className="w-4 h-4 text-accent" />
            Speed: {rate.toFixed(1)}x
          </label>
          <input type="range" min="0.5" max="2" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full h-2 rounded-lg bg-muted appearance-none cursor-pointer accent-accent" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Volume2 className="w-4 h-4 text-accent" />
            Pitch: {pitch.toFixed(1)}
          </label>
          <input type="range" min="0.5" max="2" step="0.1" value={pitch} onChange={(e) => setPitch(Number(e.target.value))} className="w-full h-2 rounded-lg bg-muted appearance-none cursor-pointer accent-accent" />
        </div>
      </div>

      {/* Playback Controls */}
      <div className="flex gap-3 mb-6">
        {!isSpeaking ? (
          <button onClick={handleSpeak} className="flex-1 py-4 rounded-xl bg-gradient-to-r from-accent to-accent/80 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg shadow-accent/25 hover:shadow-accent/40 hover:-translate-y-0.5 transition-all">
            <Play className="w-5 h-5" /> Speak Text
          </button>
        ) : (
          <>
            {isPaused ? (
              <button onClick={resume} className="flex-1 py-4 rounded-xl bg-gradient-to-r from-accent to-accent/80 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:-translate-y-0.5 transition-all">
                <Play className="w-5 h-5" /> Resume
              </button>
            ) : (
              <button onClick={pause} className="flex-1 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-500/80 text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:-translate-y-0.5 transition-all">
                <Pause className="w-5 h-5" /> Pause
              </button>
            )}
            <button onClick={stop} className="px-6 py-4 rounded-xl bg-destructive text-white font-bold flex items-center gap-2 hover:bg-destructive/90 transition-all">
              <Square className="w-5 h-5" /> Stop
            </button>
          </>
        )}
      </div>

      {/* Voice Visualizer */}
      {isSpeaking && (
        <div className="flex items-center justify-center gap-1 h-12">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="w-1.5 bg-gradient-to-t from-accent to-primary rounded-full animate-sound-bar"
              style={{
                height: `${20 + Math.random() * 80}%`,
                animationDelay: `${i * 0.05}s`,
                animationDuration: `${0.3 + Math.random() * 0.4}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Available Voices Info */}
      <div className="mt-6 p-4 rounded-xl bg-muted/50 border border-border">
        <div className="flex items-center gap-2 text-sm font-medium mb-2">
          <Languages className="w-4 h-4 text-accent" />
          Available Voices: {voices.length}
        </div>
        <p className="text-xs text-muted-foreground">
          Voices depend on your browser and operating system. Chrome typically offers the most voices including Google voices.
          For more voices, try different browsers or install additional language packs.
        </p>
      </div>
    </div>
  )
}

// ===== SCRIPT GENERATOR =====
function ScriptGenerator() {
  const [selectedTemplate, setSelectedTemplate] = useState<ScriptTemplateDef>(SCRIPT_TEMPLATES[0])
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [showHistory, setShowHistory] = useState(false)

  const { show } = useToast()
  const { generate, deleteScript, isGenerating, scripts, currentScript } = useScriptGenerator()

  const handleGenerate = () => {
    const script = generate(selectedTemplate, variables)
    if (script) show('Script generated!', 'success')
  }

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content)
    show('Script copied!', 'success')
  }

  const handleUseTemplate = (templateId: string) => {
    const template = SCRIPT_TEMPLATES.find(t => t.id === templateId)
    if (template) {
      setSelectedTemplate(template)
      setVariables({})
      show(`Loaded ${template.name} template`, 'info')
    }
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-2xl shadow-primary/5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/40 to-transparent" />

      {/* Template Selector */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold mb-3">
          <BookOpen className="w-4 h-4 text-green-500" />
          Choose Template
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SCRIPT_TEMPLATES.map((template) => (
            <button
              key={template.id}
              onClick={() => handleUseTemplate(template.id)}
              className={cn(
                'p-4 rounded-xl border text-left transition-all',
                selectedTemplate.id === template.id
                  ? 'bg-green-500/10 border-green-500/30 text-green-400'
                  : 'bg-muted border-border hover:border-green-500/20'
              )}
            >
              <div className="font-semibold text-sm mb-1">{template.name}</div>
              <div className="text-xs text-muted-foreground">{template.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Variables */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-sm font-semibold mb-3">
          <Sparkles className="w-4 h-4 text-green-500" />
          Fill in the details
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {selectedTemplate.variables.map((variable) => (
            <div key={variable}>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block capitalize">
                {variable.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="text"
                value={variables[variable] || ''}
                onChange={(e) => setVariables(prev => ({ ...prev, [variable]: e.target.value }))}
                placeholder={`Enter ${variable}...`}
                className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-sm focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-500/40 transition-all"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Generate */}
      <button onClick={handleGenerate} disabled={isGenerating} className={cn('w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all duration-300', isGenerating ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-gradient-to-r from-green-500 to-green-500/80 text-white shadow-lg shadow-green-500/25 hover:shadow-green-500/40 hover:-translate-y-0.5')}>
        {isGenerating ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</> : <><FileText className="w-5 h-5" /> Generate Script</>}
      </button>

      {/* Result */}
      {currentScript && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-lg font-semibold">
              <CheckCircle className="w-5 h-5 text-green-500" /> {currentScript.title}
            </div>
            <button onClick={() => handleCopy(currentScript.content)} className="px-4 py-2 rounded-lg bg-muted border border-border text-sm font-medium hover:bg-muted/80 transition-colors flex items-center gap-2">
              <Copy className="w-4 h-4" /> Copy
            </button>
          </div>
          <div className="p-6 rounded-xl bg-muted border border-border">
            <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">{currentScript.content}</pre>
          </div>
        </motion.div>
      )}

      {/* History */}
      {scripts.length > 1 && (
        <div className="mt-6">
          <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <BookOpen className="w-4 h-4" />
            {showHistory ? 'Hide' : 'Show'} History ({scripts.length - 1})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {scripts.slice(1).map((script) => (
                <div key={script.id} className="p-3 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
                  <div className="text-sm truncate flex-1 mr-4">{script.title}</div>
                  <div className="flex gap-2">
                    <button onClick={() => handleCopy(script.content)} className="p-1.5 rounded hover:bg-muted transition-colors">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => deleteScript(script.id)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ===== SHARED COMPONENTS =====
function SelectField({ label, icon, value, onChange, options }: {
  label: string
  icon: React.ReactNode
  value: string
  onChange: (v: string) => void
  options: { label: string; value: string }[]
}) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-semibold mb-2">
        {icon}
        {label}
      </label>
      <div className="relative">
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer">
          {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
      </div>
    </div>
  )
}
